import {
  AiAssistantBlock,
  AiChatMessage,
  AiChartPayload,
  AiPromptAudit,
  AiSuggestion,
  AiTaskItem,
} from '../models/ai-assistant.models';

export const AI_WELCOME_SUGGESTIONS: AiSuggestion[] = [
  {
    id: 'deployed-status',
    label:
      'How many tasks are currently "Deployed" and what is their status description?',
    query:
      'How many tasks are currently "Deployed" and what is their status description?',
  },
  {
    id: 'status-counts',
    label: 'List the counts for Deployed, Audited, Verified, and Do Soon tasks.',
    query: 'List the counts for Deployed, Audited, Verified, and Do Soon tasks.',
  },
  {
    id: 'security-score',
    label: 'What is the current security score shown on the dashboard?',
    query: 'What is the current security score shown on the dashboard?',
  },
  {
    id: 'risk-trend',
    label: "What is the trend in risk mitigation over the months?",
    query: "What is the trend in risk mitigation over the months?",
  },
  {
    id: 'readiness',
    label: 'What is the readiness percentage for each compliance standard?',
    query: 'What is the readiness percentage for each compliance standard?',
  },
  {
    id: 'distribution',
    label: 'What is the distribution of tasks?',
    query: 'What is the distribution of tasks?',
  },
];

export const AI_TASKS: AiTaskItem[] = [
  {
    taskId: 'END.101.03',
    title: 'Prohibit Installation Without Privileged Status',
    priority: 'Do Soon',
    risk: 'Critical',
    status: 'Deployed',
  },
  {
    taskId: 'END.101.04',
    title: 'Malicious Code Protection (Anti-Malware)',
    priority: 'Do Now',
    risk: 'High',
    status: 'Verified',
  },
  {
    taskId: 'END.101.05',
    title: 'Software Firewall',
    priority: 'Do Later',
    risk: 'Medium',
    status: 'Audited',
  },
  {
    taskId: 'END.101.06',
    title: 'Endpoint File Integrity Monitoring (FIM)',
    priority: 'Do Later',
    risk: 'Medium',
    status: 'Audited',
  },
  {
    taskId: 'END.101.07',
    title: 'Host Intrusion Detection and Prevention Systems',
    priority: 'Do Soon',
    risk: 'Low',
    status: 'Compensated',
  },
  {
    taskId: 'END.101.08',
    title: 'Phishing & Spam Protection',
    priority: 'Do Soon',
    risk: 'Low',
    status: 'To Do',
  },
];

export const AI_PRIORITY_CHART: AiChartPayload = {
  title: 'Task Priority Distribution',
  maxValue: 2000,
  data: [
    {
      label: 'Do Later',
      value: 1834,
      color: '#17c653',
      description: 'Highest workload; tasks can be postponed without urgency.',
    },
    {
      label: 'Do Now',
      value: 1580,
      color: '#f8285a',
      description: 'Most time-sensitive tasks requiring immediate action.',
    },
    {
      label: 'Do Soon',
      value: 1652,
      color: '#f6a609',
      description: 'Medium-urgency tasks that need attention after immediate work.',
    },
  ],
};

export const AI_AUDIT_PROMPT: AiPromptAudit = {
  id: 'audit-dashboard-task-query',
  systemPrompt:
    'You are Octy, an eCISO dashboard assistant. Answer with concise dashboard-grounded summaries and typed UI blocks.',
  userPrompt:
    'Resolve the user question against dashboard task metrics using available mock task, priority, risk, and status aggregates.',
  interpretedIntent: 'dashboard_task_insight',
  tool: 'mock.dashboard.taskInsight',
  payload: {
    source: 'mock-data',
    filters: {
      assigned: 'current-user',
      dashboard: 'my-dashboard',
    },
    renderBlocks: ['card-narration', 'task-list', 'narration-graph', 'follow-up'],
  },
};

const deployedBlocks: AiAssistantBlock[] = [
  {
    id: 'deployed-card',
    type: 'card-narration',
    card: {
      label: 'Deployed',
      value: '12',
      caption: 'Live & Active',
      progressLabel: 'Progress',
      progressPercent: 44,
      tone: 'success',
    },
    narration:
      'There are 12 tasks currently "Deployed", and their status is "Live & Active".',
  },
  {
    id: 'deployed-follow-up',
    type: 'follow-up',
    questions: [
      {
        id: 'follow-audited',
        label: 'Audited',
        query: 'Show me the audited task count.',
      },
      {
        id: 'follow-verified',
        label: 'Verified',
        query: 'Show me the verified task count.',
      },
      {
        id: 'follow-do-soon',
        label: 'Do Soon',
        query: 'Show me the Do Soon task count.',
      },
    ],
  },
];

const taskListBlocks: AiAssistantBlock[] = [
  {
    id: 'total-task-list',
    type: 'task-list',
    title: 'Total Task (122)',
    totalLabel: 'Total Task (122)',
    tasks: AI_TASKS,
  },
];

const priorityGraphBlocks: AiAssistantBlock[] = [
  {
    id: 'priority-graph',
    type: 'narration-graph',
    chart: AI_PRIORITY_CHART,
    narration: [
      'Do Later has the highest workload at 1834 tasks, meaning many items can be planned without immediate urgency.',
      'Do Now has 1580 tasks and should be treated as the most time-sensitive queue.',
      'Do Soon has 1652 tasks and is the medium-urgency group to handle after immediate work.',
      'Overall insight: start with Do Now, move to Do Later, and finish with Do Soon to maintain an efficient workflow.',
    ],
  },
  {
    id: 'priority-follow-up',
    type: 'follow-up',
    questions: [
      {
        id: 'follow-status-progress',
        label: 'How does each status affect progress?',
        query: 'Do you want insights into how each task status contributes to the overall progress?',
      },
      {
        id: 'follow-task-chart',
        label: 'Task chart summary',
        query: 'Give me the task chart summary.',
      },
    ],
  },
];

const cldBlocks: AiAssistantBlock[] = [
  {
    id: 'cld-jayesh',
    type: 'only-narration',
    narration: 'There are 22 tasks under CLD, assigned to Jayesh.',
    contacts: [
      { name: 'Jayesh Patil', email: 'jayeshpatil@gmail.com' },
      { name: 'Jayesh Chaudhari', email: 'JayeshChudhari@gmail.com' },
      { name: 'Jayesh Gujar', email: 'jayeshgujar@gmail.com' },
    ],
  },
];

const statusCountBlocks: AiAssistantBlock[] = [
  {
    id: 'status-counts',
    type: 'only-narration',
    narration:
      'Current mock status counts are Deployed: 12, Audited: 2, Verified: 6, and Do Soon: 18.',
  },
  {
    id: 'status-count-follow-up',
    type: 'follow-up',
    questions: [
      {
        id: 'status-to-total',
        label: 'Show total task list',
        query: 'How many total tasks are there?',
      },
    ],
  },
];

export const AI_RESPONSE_LIBRARY: Record<string, AiAssistantBlock[]> = {
  deployed: deployedBlocks,
  totalTasks: taskListBlocks,
  priorityChart: priorityGraphBlocks,
  cldJayesh: cldBlocks,
  statusCounts: statusCountBlocks,
  securityScore: [
    {
      id: 'security-score',
      type: 'only-narration',
      narration:
        'The current dashboard security score is 31% in the mock dashboard context.',
    },
  ],
  default: [
    {
      id: 'default-narration',
      type: 'only-narration',
      narration:
        'I can help inspect dashboard task totals, status counts, priority distribution, risk trends, and prompt audit details using mock data.',
    },
  ],
};

export const AI_SEEDED_MESSAGES: AiChatMessage[] = [];
